import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchMedicalConsent, 
  fetchPatientData, 
  fetchMedicalNextIp, 
  submitMedicalConsent, 
  updateMedicalConsent,
  generateMedicalShareToken
} from "../api/consentApi";
import { mapBackendToForm, mapFormToBackend } from "../utils/mapping";
import { useFormValidation } from "./useFormValidation";
import { useNotification } from "../../../../contexts/NotificationContext";
import { DRAFT_KEYS, loadDraft, clearDraft } from "../../../../utils/draftUtils";
import { syncPatientData, getSyncedPatientData, SURGERY_TEMPLATES } from "../../../../utils/formSync";
import { signatureDatetimeMap } from "../constants/options";

export const useConsentForm = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [success, setSuccess] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    operation_procedure: "",
    advised_of_major_risk: "",
    care_plan: "",
    care_plan_dr: "",
    care_plan_options: "",
    care_plan_risks: "",
    care_plan_benefits: "",
    authorize_dr: "",
    authorize_by: "",
    diagnosis: "",
    diagnosis_dr: "",
    diagnosis_like: "",
    condition_like: "",
    understand_by_dr: "",
    i_understood_by_dr: "",
    patient_signature: "",
    patient_name: "",
    patient_signature_date_time: "",
    witness_signature: "",
    witness_name: "",
    witness_signature_date_time: "",
    doctor_anaesthetist_signature: "",
    doctor_anaesthetist_name: "",
    doctor_anaesthetist_signature_date_time: "",
    interpreter_signature: "",
    interpreter_name: "",
    interpreter_signature_date_time: "",
    unable_consent_reason: "",
    attender_name: "",
    patient_rep_signature: "",
    patient_rep_name: "",
    patient_rep_signature_date_time: "",
    patient_rep_witness_signature: "",
    patient_rep_witness_name: "",
    patient_rep_witness_date_time: "",
    patient_rep_doctor_signature: "",
    patient_rep_doctor_name: "",
    patient_rep_doctor_signature_date_time: "",
    patient_rep_interpreter_signature: "",
    patient_rep_interpreter_name: "",
    patient_rep_interpreter_signature_date_time: ""
  });

  const { errors, setErrors, clearError, validateSubmit } = useFormValidation(formData);

  // Fetch Patient Data
  const { data: patientInfo } = useQuery({
    queryKey: ['patient', uhid_no],
    queryFn: () => fetchPatientData(uhid_no),
    enabled: !!uhid_no
  });

  // Fetch Next IP No
  const { data: nextIpData } = useQuery({
    queryKey: ['medicalNextIp'],
    queryFn: fetchMedicalNextIp,
    enabled: !success
  });

  // Fetch Existing Consent Data
  const { data: existingData, isLoading: dataLoading } = useQuery({
    queryKey: ['medicalConsent', uhid_no],
    queryFn: () => fetchMedicalConsent(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  // Handle existing data load
  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      const mapped = mapBackendToForm(existingData);
      setFormData(prev => ({ ...prev, ...mapped }));
      setSuccess(true);
    }
  }, [existingData]);

  // Handle patient data load
  useEffect(() => {
    if (patientInfo) {
      setFormData(prev => ({
        ...prev,
        patient_name: patientInfo.name
      }));
    }
  }, [patientInfo]);

  // Handle next IP load
  useEffect(() => {
    if (nextIpData && !success) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, success]);

  // Handle auto-sync
  useEffect(() => {
    if (uhid_no && !success) {
      const syncedData = getSyncedPatientData(uhid_no);
      if (Object.keys(syncedData).length > 0) {
        setFormData(prev => ({
          ...prev,
          diagnosis: prev.diagnosis || syncedData.diagnosis || "",
          operation_procedure: prev.operation_procedure || syncedData.operation_procedure || "",
          ip_no: prev.ip_no || syncedData.ip_no || ""
        }));
        showNotification('info', 'Smart Prefill: Loaded data from other forms.');
      }
    }
  }, [uhid_no, success]);

  // Handle draft
  useEffect(() => {
    const draft = loadDraft(DRAFT_KEYS.CONSENT_FOR_MEDICAL, uhid_no);
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
    }
  }, [uhid_no]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Cross-form sync
    const syncableFields = ['diagnosis', 'operation_procedure', 'ip_no'];
    if (syncableFields.includes(name)) {
      syncPatientData(uhid_no, { [name]: newValue });
    }

    if (errors[name]) {
      clearError(name);
    }
  }, [uhid_no, errors, clearError]);

  const handleNameChange = useCallback((e) => {
    const { name, value } = e.target;
    const regex = /^[a-zA-Z\s.-]*$/;
    if (value === '' || regex.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
      if (errors[name]) {
        clearError(name);
      }
    }
  }, [errors, clearError]);

  const applyTemplate = useCallback((procedure) => {
    const template = SURGERY_TEMPLATES[procedure];
    if (template) {
      setFormData(prev => ({
        ...prev,
        operation_procedure: procedure,
        care_plan_risks: template.complications,
        care_plan_benefits: "Better recovery, pain relief.",
        advised_of_major_risk: template.risks
      }));
      showNotification('success', `Applied surgery template for ${procedure}`);
    }
  }, []);

  const handleSignatureOpen = (signatureKey) => {
    setCurrentSignatureRole(signatureKey);
    const datetimeKey = signatureDatetimeMap[signatureKey];
    if (datetimeKey) {
      const now = new Date().toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, [datetimeKey]: now }));
    }
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = (signatureDataURL) => {
    setFormData(prev => ({
      ...prev,
      [currentSignatureRole]: signatureDataURL.split(',')[1]
    }));
    setSignatureDialogOpen(false);
  };

  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updateMedicalConsent(uhid_no, data);
      } else {
        return submitMedicalConsent(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success 
        ? 'Medical/Surgical Consent Form updated successfully!' 
        : 'Medical/Surgical Consent Form submitted successfully!');
      
      queryClient.invalidateQueries(['medicalConsent', uhid_no]);
      setSuccess(true);
      clearDraft(DRAFT_KEYS.CONSENT_FOR_MEDICAL, uhid_no);
      navigate(`/mainPage/${uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    },
    onError: (error) => {
      console.error('Error submitting the form!', error);
      showNotification('error', 'Error in submitting Medical/Surgical Consent Form!');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mutation.isLoading) return;

    if (!validateSubmit()) {
      return;
    }

    const backendData = mapFormToBackend(formData);
    mutation.mutate(backendData);
  };

  const handleShare = async () => {
    try {
      const { token } = await generateMedicalShareToken(uhid_no);
      const link = `${window.location.origin}/consentForMedical/${uhid_no}?token=${token}`;
      const message = `Dear Doctor,\n\nPlease fill the medical/surgical consent form:\n\n${link}`;

      await navigator.clipboard.writeText(link);
      showNotification('info', "Secure link copied to clipboard!");

      const phoneNumber = '+916379605575'; 
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      showNotification('error', "Failed to generate shareable link");
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    loading: dataLoading || mutation.isLoading,
    success,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    applyTemplate,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare
  };
};
